/**
 * Handles loading of resource sets.
 * Each resource is loaded asynchronously by corresponding loader and when all resources are ready, onLoad callback is invoked
 */
import {Slots} from "./Slots";
import {ObjectLoader} from "./ObjectLoader";
import {App3D} from "./App3D";

import * as _ from "lodash";
import {ComposedLoadingProgress, LoadingProgress} from "./LoadingProgress";

export class RequestStatus{
	protected _loaded: boolean = false;

	constructor(
		protected progress:LoadingProgress
	) {}

	get loaded():boolean{
		return this._loaded;
	}

	/**
	 * Marks status as loaded
	 */
	finish(){
		this.progress.resolve();
		this._loaded = true;
	}
}

export class ResourceManager {
	private cache: {} = {} as any;
	protected _progress:LoadingProgress = new LoadingProgress();

	/**
	 * Requested resources (true if loaded, false if not)
	 */
	protected requests: { [url:string]: RequestStatus } = {};

	constructor(
		public loader,
		public name = null) {
	}

	/**
	 * Indicates, thas this manager does not load resources
	 * @param sID
	 */
	static noLoad(sID) {
		return (sID == 'uniforms');
	}

	/**
	 * Loads multiple resources of same type and calls callback when all of them are loaded
	 * @param set list of items to be loaded as [id: path]
	 * @param {function} onLoad function to be called when all resources are loaded
	 * @param {boolean=false} index Whether to index by file or by ID (default)
	 */
	get(set, onLoad, index = false) {

		// updateds cache and re-checks if all requests are loaded
		let loadCheck = (path = null, data = null) => {
			if (path)
				this.cache[path] = data;	// save to cache

			let items = this.checkItems(set, index);
			if (items) {
				onLoad(items);
				return true;  // all is loaded, no need to do anything else
			}
			return false;  // something is missing
		};

		// check cache before loading
		if (loadCheck())
			return;

		_.each(set, (path, id) => {
			if (this.cache.hasOwnProperty(id))
				return;
			if (path){
				if (!this.requests.hasOwnProperty(path))
					this.requests[path] = new RequestStatus(this._progress.add());
				this.loader.load(path, (data) => {
					loadCheck(path, data);
					this.requests[path].finish();
				});
			}
			else
				if (loadCheck(id))
					return false;
		});
	}

	/**
	 * Recheck if all items from set are loaded
	 * @param {Array} set Set of IDs to be checked
	 * @param {boolean=false} index Whether to index by file URL or by ID (default)
	 * @returns {Array|boolean} List of items if all are available or false if some still missing
	 */
	checkItems(set, index = false) {
		let done = true;
		let items = {} as any;
		_.each(set, (file, id) => {
			if (this.cache.hasOwnProperty(file))	// check cache
				items[index ? file : id] = this.cache[file];
			else if (file) {	// file is not loaded yet
				done = false;
				return false;
			}
		});
		return done
			? items
			: false;
	}

	clearCache() {
		this.cache = {} as any;
	}

	/**
	 * Use new LoadingProgress for all following requests
	 */
	flushProgress(): LoadingProgress{
		this._progress = new LoadingProgress();
		return this._progress;
	}

	get progress(): LoadingProgress{
		return this._progress;
	}
}

/**
 * Set of different types of resources (models, textures, shaders..)
 */
export class ManagerSet {
	private onLoad:(data) => void;
	private managers: { [id:string]: ResourceManager } = {} as any;
	private requests: { [id:string]: { [id:string]:string } } = {} as any;
	protected _progress:ComposedLoadingProgress = new ComposedLoadingProgress();

	/**
	 * Set of several resource managers (for each type of resource)
	 * @param app3D
	 * @param {object} managers list of loaders to be used to load resources (indexed by resource type)
	 * @constructor
	 */
	constructor(
			protected app3D:App3D,
			managers) {
		this.onLoad = null;
		_.each(managers, (loader, mID) => {
			this.managers[mID] = new ResourceManager(loader, mID);
			this.requests[mID] = {} as any; // global data
			this._progress.add(mID, this.managers[mID].progress);
		});
	}

	/**
	 * Register function to be performed when everything is loaded
	 * @param fnc
	 */
	onDone(fnc:(data:any) => void) {
		this.onLoad = fnc;
		this.checkAll(this.managers);
	}

	clearCache() {
		_.each(this.managers, (man) => {
			man.clearCache();
		});
	}

	/**
	 * Loads resources by corresponding loaders and calls onLoad when all of them are loaded
	 * @param {object} sets sets to be loaded indexed by resource type
	 * @param {function} onLoad Function to be called when all resources are loaded
	 * @param {string} meta Metadata related to loaded resource (for debugging purposes)
	 * @param {Slots} slotListing Slots container, which will gather all slots found during model loading
	 */
	get(sets, onLoad, meta: string = 'unknown', slotListing: Slots=null) {
		let managers = this.managers;
		// create alternative object loader, which will register slot information to proper slotListing
		if (slotListing){
			let objectLoader = new ObjectLoader(this.app3D, slotListing);
			objectLoader.setPath(this.app3D.config.model_path);
			managers['objects'] = new ResourceManager(objectLoader, 'object');
		}
		let checker = new Checker(this.app3D, sets, (items) => {
			if (onLoad)
				onLoad(items);
			this.checkAll(managers);
		});
		_.each(sets, (set, sID) => {
			if (ResourceManager.noLoad(sID)) {
				checker.addSet(sID, true);
				return;
			}

			if (!managers.hasOwnProperty(sID))
				throw 'Invalid set index: ' + sID;
			// add to global resource list
			_.each(set, (file, id) => {
				if (!file){
					if (sets[sID][id])
						console.warn(`${sID}/${id} of ${meta} is missing.`);
					// Make sure there are no undefined textures, so fragment shader will not silently crash
					if (sID == 'textures'){
						file =
						set[id] = 'white.png';
					}
				}
				this.requests[sID][file] = file;
			});
			/*
			 Go through all sets.
			 When each set is loaded, it checks other sets and if all other are loaded, call onload callback.
			 */
			managers[sID].get(set, (loadedSet) => {
				checker.addSet(sID, loadedSet);
			}, true);
			checker.check();
		});
	}

	/**
	 * Checks all requests and performs onload call if loaded
	 */
	checkAll(managers) {
		if (!this.onLoad) return;
		let allDone = true;
		let data = {} as any;
		_.each(this.requests, (set, sID) => {
			if (ResourceManager.noLoad(sID))
				return true;
			data[sID] = managers[sID].checkItems(set, true);
			if (!data[sID]) {
				allDone = false;
				return false;
			}
		});
		if (allDone) {
			this.onLoad(data);
			this.onLoad = null; // do not call this one again
		}
	}

	get progress(): ComposedLoadingProgress{
		return this._progress;
	}

	/**
	 * Use new LoadingProgress for all following requests
	 */
	flushProgress(): ComposedLoadingProgress{
		this._progress = new ComposedLoadingProgress();
		_.each(this.managers, (manager, mID) => {
			this._progress.add(mID, manager.flushProgress());
		});
		return this._progress;
	}
}

/** Monitors set loading */
export class Checker {
	protected loadedSets: {} = {} as any;

	constructor(
		protected app3D: App3D,
		public sets,
		public callback) {
	}

	/** Checks, whether all of requested sets are loaded. If yes, execute calllback. */
	check() {
		let items = this.checkItems(this.sets, this.loadedSets);
		if (items && this.callback) {
			this.callback(items);
			this.callback = null;
		}
	};

	/** Add set and checks for completition */
	addSet(id, data) {
		this.loadedSets[id] = data;
		this.check();
	}

	protected checkItems(sets, loadedSets) {
		let done = true;
		let data = {} as any;

		_.each(sets, (set, sID) => {
			if (ResourceManager.noLoad(sID))
				return true;
			data[sID] = {} as any;
			if (loadedSets.hasOwnProperty(sID)) {
				_.each(set, (path, rID) => {
					if (loadedSets[sID].hasOwnProperty(path))
						data[sID][rID] = loadedSets[sID][path];
					else if (path) {  // item in set is missing
						done = false;
						return false;
					}
					else
						console.warn(`Expected resource is undefined: ${sID}/${rID}`);
				});
			}
			else {  // whole set is missing
				done = false;
				return false;
			}
		});
		return done
				? data
				: false;
	}
}



// WEBPACK FOOTER //
// ./src/script/app3D/ResourceManager.ts